/**
 * "Online now" widget (web only) for the right nav.
 *
 * Lists logged-in atproto users currently connected to the presence socket
 * (excludes yourself and did:key guests, who have no profile). Clicking a user
 * RINGS them for a 1:1 video call: the worker pushes an incoming-call event to
 * the callee and returns the pair-room slug, which the caller navigates into to
 * wait for them to answer. Renders nothing when nobody else is online.
 */

import {Pressable, View} from 'react-native'
import {type AppBskyActorDefs} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {logger} from '#/logger'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {useOnlineDids} from '#/state/presence'
import {ringUser} from '#/state/presence/ring'
import {useProfilesQuery} from '#/state/queries/profile'
import {useAgent, useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {VideoClip_Stroke2_Corner0_Rounded as VideoIcon} from '#/components/icons/VideoClip'
import * as ProfileCard from '#/components/ProfileCard'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {navigate} from '#/Navigation'

const MAX_ROWS = 10

export function OnlineNowWidget() {
  const t = useTheme()
  const {onlineDids} = useOnlineDids()
  const {currentAccount} = useSession()
  const moderationOpts = useModerationOpts()

  const others = onlineDids
    .filter(did => did !== currentAccount?.did && !did.startsWith('did:key'))
    .slice(0, MAX_ROWS)

  const {data} = useProfilesQuery({handles: others})
  const profiles = data?.profiles ?? []

  if (others.length === 0 || !moderationOpts) {
    return null
  }

  return (
    <View style={[a.gap_sm, a.mt_md]}>
      <View style={[a.flex_row, a.align_center, a.gap_sm]}>
        <View
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: t.palette.positive_500,
          }}
        />
        <Text style={[a.text_sm, a.font_bold, t.atoms.text_contrast_medium]}>
          <Trans>Online now</Trans>
        </Text>
      </View>

      <View style={[a.gap_xs]}>
        {profiles.map(profile => (
          <OnlineUserRow
            key={profile.did}
            profile={profile}
            moderationOpts={moderationOpts}
          />
        ))}
      </View>
    </View>
  )
}

function OnlineUserRow({
  profile,
  moderationOpts,
}: {
  profile: AppBskyActorDefs.ProfileViewDetailed
  moderationOpts: NonNullable<ReturnType<typeof useModerationOpts>>
}) {
  const t = useTheme()
  const {_} = useLingui()
  const agent = useAgent()
  const {currentAccount} = useSession()

  const onPress = () => {
    void (async () => {
      try {
        const {roomSlug} = await ringUser(
          agent,
          profile.did,
          currentAccount?.handle,
        )
        void navigate('Call', {roomId: roomSlug})
      } catch (err) {
        logger.warn('ringUser failed', {safeMessage: err})
        Toast.show(_(msg`Couldn't start the call`))
      }
    })()
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={_(msg`Call ${profile.handle}`)}
      accessibilityHint=""
      onPress={onPress}
      style={[a.flex_row, a.align_center, a.rounded_sm, a.px_xs, a.py_2xs]}>
      <View style={[a.flex_1]}>
        <ProfileCard.Header>
          <ProfileCard.Avatar
            profile={profile}
            moderationOpts={moderationOpts}
            size={32}
            disabledPreview
          />
          <ProfileCard.NameAndHandle
            profile={profile}
            moderationOpts={moderationOpts}
          />
        </ProfileCard.Header>
      </View>
      <VideoIcon size="sm" fill={t.atoms.text_contrast_medium.color} />
    </Pressable>
  )
}
